import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { PageHeader } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Building2,
  BriefcaseBusiness,
  Send,
  CalendarClock,
  Award,
  Users,
  Handshake,
  Receipt,
  ClipboardList,
  ChevronRight,
  ExternalLink,
  Loader2,
  BarChart3,
} from "lucide-react";
import { fmtDate, money, timeAgo } from "@/lib/format";

// Map dashboard "route" values to a fetch config (API path + which record fields to show).
const ROUTE_TO_API = {
  "/clients": { api: "/clients", cols: ["company_name", "industry", "region", "status"], detail: (r) => `/clients/${r.id}` },
  "/contacts": { api: "/contacts", cols: ["name", "email", "phone", "company_name"], detail: null },
  "/jobs": { api: "/jobs", cols: ["title", "client_name", "status", "region"], detail: (r) => `/jobs/${r.id}` },
  "/candidates": { api: "/candidates", cols: ["full_name", "current_title", "current_employer", "status"], detail: (r) => `/candidates/${r.id}` },
  "/submissions": { api: "/submissions", cols: ["candidate_name", "job_title", "client_name", "stage"], detail: null },
  "/interviews": { api: "/interviews", cols: ["candidate_name", "job_title", "date", "status"], detail: null },
  "/offers": { api: "/offers", cols: ["candidate_name", "client_name", "final_accepted_amount", "status"], detail: null },
  "/placements": { api: "/placements", cols: ["candidate_name", "client_name", "job_title", "status"], detail: null },
  "/revenue": { api: "/invoices", cols: ["invoice_number", "client_name", "candidate_name", "total", "status"], detail: null },
  "/tasks": { api: "/tasks", cols: ["title", "type", "priority", "due_at", "status"], detail: null },
  "/ra/leads": { api: "/ra/leads", cols: ["company_name", "industry", "overall_status"], detail: null },
  "/ra/replies": { api: "/ra/leads", cols: ["company_name", "overall_status"], detail: null },
  "/ra/outreach": { api: "/ra/leads", cols: ["company_name", "overall_status"], detail: null },
  "/sales-pipeline": { api: "/sales/handoffs", cols: ["client_name", "raised_by_name", "decision"], detail: null },
  "/hrms/employees": { api: "/hrms/employees", cols: ["name", "email", "role", "department"], detail: (r) => `/hrms/employees/${r.id}` },
  "/hrms/leaves": { api: "/hrms/leaves/requests", cols: ["user_name", "leave_type", "start_date", "end_date", "status"], detail: null },
  "/hrms/attendance": { api: "/hrms/attendance/records", cols: ["user_name", "date", "check_in", "check_out"], detail: null },
  "/hrms/payroll": { api: "/hrms/payroll/runs", cols: ["period", "status", "run_at"], detail: null },
  "/hrms/payslips": { api: "/hrms/payroll/payslips", cols: ["user_name", "period", "net_pay"], detail: null },
};

const DEPT_META = {
  commercial: { icon: Building2, tone: "text-blue-700" },
  ra: { icon: BarChart3, tone: "text-violet-700" },
  recruitment: { icon: Users, tone: "text-emerald-700" },
  hrms: { icon: Users, tone: "text-orange-700" },
  placements: { icon: Award, tone: "text-teal-700" },
  offers: { icon: Handshake, tone: "text-amber-700" },
  finance: { icon: Receipt, tone: "text-emerald-700" },
};

const SECTION_ICONS = {
  commercial: Building2,
  jobs: BriefcaseBusiness,
  pipeline: Send,
  finance: Receipt,
  ra: BarChart3,
  hrms: Users,
  activities: ClipboardList,
};

export default function ExecutiveDashboard() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [deptData, setDeptData] = useState({});
  const [activeDept, setActiveDept] = useState("commercial");
  const [drawer, setDrawer] = useState(null); // { title, route, params, items, total, loading }
  const [memberDrawer, setMemberDrawer] = useState(null); // { dept, member, data, loading }

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/executive/overview");
        setOverview(data);
      } catch (e) {
        toast.error(apiErr(e));
      }
    })();
  }, []);

  const loadDept = async (key) => {
    if (deptData[key]) return deptData[key];
    try {
      const { data } = await api.get(`/executive/department/${key}`);
      setDeptData((prev) => ({ ...prev, [key]: data }));
      return data;
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  useEffect(() => {
    loadDept(activeDept);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDept]);

  const openKpi = async (label, link) => {
    if (!link || !link.route) return;
    const cfg = ROUTE_TO_API[link.route];
    if (!cfg) {
      // Fallback: navigate to the page directly with query params
      const qs = new URLSearchParams(link.params || {}).toString();
      navigate(qs ? `${link.route}?${qs}` : link.route);
      return;
    }
    setDrawer({ title: label, route: link.route, params: link.params || {}, items: null, total: 0, cfg });
    try {
      const { data } = await api.get(cfg.api, { params: { ...link.params, page_size: 100 } });
      setDrawer({
        title: label,
        route: link.route,
        params: link.params || {},
        items: data.items || [],
        total: data.total ?? (data.items || []).length,
        cfg,
      });
    } catch (e) {
      setDrawer({
        title: label,
        route: link.route,
        params: link.params || {},
        items: [],
        total: 0,
        cfg,
        error: apiErr(e),
      });
    }
  };

  const openMember = async (dept, member) => {
    setMemberDrawer({ dept, member, data: null, loading: true });
    try {
      const { data } = await api.get(`/executive/department/${dept}/member/${member.id}`);
      setMemberDrawer({ dept, member, data, loading: false });
    } catch (e) {
      toast.error(apiErr(e));
      setMemberDrawer(null);
    }
  };

  if (!overview) {
    return (
      <div className="space-y-4">
        <PageHeader title="Executive Command Center" subtitle="Loading..." />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {Array.from({ length: 12 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Executive Command Center"
        subtitle={`Company-wide view · As of ${fmtDate(overview.as_of)}`}
      />

      {/* Company overview — every department represented equally */}
      <div className="space-y-4">
        {Object.entries(overview.sections).map(([key, section]) => {
          const Icon = SECTION_ICONS[key] || BarChart3;
          return (
            <div key={key}>
              <div className="flex items-center gap-2 mb-2">
                <Icon className="h-4 w-4 text-[#255058]" />
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  {section.title}
                </h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                {section.kpis.map((kpi, i) => (
                  <KpiTile key={i} kpi={kpi} onClick={openKpi} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Department drill-down */}
      <div className="mt-8">
        <h2 className="text-lg font-display font-semibold mb-3">Department Performance</h2>
        <Tabs value={activeDept} onValueChange={setActiveDept}>
          <TabsList className="flex flex-wrap h-auto">
            {overview.departments.map((d) => (
              <TabsTrigger
                key={d.key}
                value={d.key}
                data-testid={`dept-tab-${d.key}`}
              >
                {d.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {overview.departments.map((d) => (
            <TabsContent key={d.key} value={d.key} className="mt-4">
              <DepartmentPanel
                data={deptData[d.key]}
                onKpi={openKpi}
                onMember={(m) => openMember(d.key, m)}
                deptKey={d.key}
              />
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* Filtered records drawer */}
      <Sheet open={!!drawer} onOpenChange={(o) => !o && setDrawer(null)}>
        <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto">
          {drawer && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  {drawer.title}
                  <Badge variant="secondary">{drawer.total}</Badge>
                </SheetTitle>
                <SheetDescription className="flex items-center gap-2">
                  Filter: {formatParams(drawer.params)}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const qs = new URLSearchParams(drawer.params || {}).toString();
                      navigate(qs ? `${drawer.route}?${qs}` : drawer.route);
                      setDrawer(null);
                    }}
                    data-testid="drawer-open-full"
                  >
                    Open full page <ExternalLink className="h-3 w-3 ml-1" />
                  </Button>
                </SheetDescription>
              </SheetHeader>
              <div className="mt-4">
                {drawer.items === null ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading records...
                  </div>
                ) : drawer.items.length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    {drawer.error
                      ? `Could not fetch: ${drawer.error}`
                      : "No records match this filter."}
                  </div>
                ) : (
                  <RecordsTable cfg={drawer.cfg} items={drawer.items} onOpen={(row) => {
                    const detail = drawer.cfg.detail && drawer.cfg.detail(row);
                    if (detail) {
                      navigate(detail);
                      setDrawer(null);
                    }
                  }} />
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Member drill-down drawer */}
      <Sheet open={!!memberDrawer} onOpenChange={(o) => !o && setMemberDrawer(null)}>
        <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto">
          {memberDrawer && (
            <>
              <SheetHeader>
                <SheetTitle>{memberDrawer.member.name}</SheetTitle>
                <SheetDescription>
                  {memberDrawer.member.role} · {memberDrawer.member.email}
                </SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-4">
                {memberDrawer.loading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading...
                  </div>
                ) : (
                  <MemberDetail data={memberDrawer.data} navigate={navigate} onClose={() => setMemberDrawer(null)} />
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// -------------------- Sub-components --------------------

function KpiTile({ kpi, onClick }) {
  const clickable = !!kpi.link;
  const tone =
    kpi.tone === "red"
      ? "border-red-200 bg-red-50/40"
      : kpi.tone === "amber"
      ? "border-amber-200 bg-amber-50/40"
      : "";
  return (
    <button
      type="button"
      onClick={() => clickable && onClick(kpi.label, kpi.link)}
      disabled={!clickable}
      data-testid={`kpi-${kpi.label.replace(/\s+/g, "-").toLowerCase()}`}
      className={`text-left rounded-lg border p-3 transition ${tone} ${
        clickable
          ? "hover:border-[#255058] hover:shadow-sm cursor-pointer"
          : "opacity-90 cursor-default"
      }`}
    >
      <div className="text-xs text-muted-foreground">{kpi.label}</div>
      <div className="text-2xl font-semibold mt-1">
        {typeof kpi.value === "number" && kpi.label.includes("$")
          ? money(kpi.value, "USD")
          : kpi.value}
      </div>
      {kpi.subtitle && (
        <div className="text-[11px] text-muted-foreground mt-0.5">
          {kpi.subtitle}
        </div>
      )}
      {clickable && (
        <div className="mt-1 text-[11px] text-[#255058] flex items-center gap-1">
          View records <ChevronRight className="h-3 w-3" />
        </div>
      )}
    </button>
  );
}

function DepartmentPanel({ data, onKpi, onMember, deptKey }) {
  if (!data) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-20" />
        ))}
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
          {data.title} — roll-up
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
          {data.kpis.map((k, i) => (
            <KpiTile key={i} kpi={k} onClick={onKpi} />
          ))}
        </div>
        {data.method_breakdown && data.method_breakdown.length > 0 && (
          <div className="mt-3 pt-3 border-t">
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
              Received By Method
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {data.method_breakdown.map((k, i) => (
                <KpiTile key={i} kpi={k} onClick={onKpi} />
              ))}
            </div>
          </div>
        )}
      </Card>

      {data.members && data.members.length > 0 && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Team Members ({data.members.length})
            </div>
            <div className="text-xs text-muted-foreground">
              Click a KPI to open filtered records
            </div>
          </div>
          <div className="space-y-3">
            {data.members.map((m) => (
              <div
                key={m.id}
                className="border rounded-lg p-3 hover:border-[#255058]"
                data-testid={`member-row-${m.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="font-medium">{m.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {m.role} · {m.email}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onMember(m)}
                    data-testid={`member-view-${m.id}`}
                  >
                    View Performance <ChevronRight className="h-3 w-3 ml-1" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {m.kpis.map((k, i) => (
                    <KpiTile key={i} kpi={k} onClick={onKpi} />
                  ))}
                </div>
                {m.targets && Object.keys(m.targets).length > 0 && (
                  <div className="mt-2 text-xs">
                    <span className="font-medium">Targets:</span>{" "}
                    {Object.entries(m.targets)
                      .map(([k, v]) => `${k}=${v}`)
                      .join(" · ")}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function RecordsTable({ cfg, items, onOpen }) {
  return (
    <div className="overflow-x-auto scrollbar-thin">
      <Table>
        <TableHeader>
          <TableRow>
            {cfg.cols.map((c) => (
              <TableHead key={c}>{humanCol(c)}</TableHead>
            ))}
            {cfg.detail && <TableHead></TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((r) => (
            <TableRow key={r.id} className="hover:bg-accent/40">
              {cfg.cols.map((c) => (
                <TableCell key={c} className="text-sm">
                  {formatCell(r, c)}
                </TableCell>
              ))}
              {cfg.detail && (
                <TableCell>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onOpen(r)}
                    data-testid={`open-record-${r.id}`}
                  >
                    Open <ChevronRight className="h-3 w-3 ml-1" />
                  </Button>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function MemberDetail({ data, navigate, onClose }) {
  if (!data) return null;
  const buckets = Object.entries(data).filter(
    ([k, v]) => v && typeof v === "object" && "items" in v && "total" in v
  );
  return (
    <div className="space-y-4">
      {data.targets && Object.keys(data.targets).length > 0 && (
        <Card className="p-3">
          <div className="text-xs font-medium mb-1">Targets</div>
          <div className="text-sm">
            {Object.entries(data.targets)
              .map(([k, v]) => `${k}: ${v}`)
              .join(" · ")}
          </div>
        </Card>
      )}
      {buckets.map(([key, bucket]) => (
        <Card key={key} className="p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium capitalize">
              {key.replace(/_/g, " ")}
            </div>
            <Badge variant="secondary">{bucket.total}</Badge>
          </div>
          {bucket.items.length === 0 ? (
            <div className="text-xs text-muted-foreground">None</div>
          ) : (
            <div className="space-y-1">
              {bucket.items.slice(0, 10).map((r) => (
                <div
                  key={r.id}
                  className="flex justify-between items-center text-xs border rounded px-2 py-1"
                >
                  <div className="truncate">{recordSummary(key, r)}</div>
                  {detailRoute(key, r) && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        navigate(detailRoute(key, r));
                        onClose();
                      }}
                    >
                      Open <ChevronRight className="h-3 w-3 ml-1" />
                    </Button>
                  )}
                </div>
              ))}
              {bucket.items.length < bucket.total && (
                <div className="text-[11px] text-muted-foreground">
                  Showing first {bucket.items.length} of {bucket.total}
                </div>
              )}
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

// -------------------- Helpers --------------------

function formatParams(p) {
  const entries = Object.entries(p || {});
  if (entries.length === 0) return "all records";
  return entries.map(([k, v]) => `${k}=${v}`).join(" · ");
}

function humanCol(c) {
  return c
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function formatCell(r, c) {
  const v = r[c];
  if (v === null || v === undefined || v === "") return "—";
  if (c.endsWith("_at") || c === "date" || c.endsWith("_date")) return fmtDate(v);
  if (c === "total" || c === "final_accepted_amount" || c === "net_pay") return money(v, r.currency || "USD");
  if (typeof v === "object") return JSON.stringify(v).slice(0, 40);
  return String(v);
}

function recordSummary(key, r) {
  if (key === "clients") return r.company_name;
  if (key === "jobs" || key === "assigned_jobs") return `${r.title} · ${r.client_name || ""}`;
  if (key === "candidates") return `${r.full_name} · ${r.current_title || ""}`;
  if (key === "submissions") return `${r.candidate_name} → ${r.client_name} (${r.stage})`;
  if (key === "interviews") return `${r.candidate_name} · ${fmtDate(r.date)} · ${r.status}`;
  if (key === "offers") return `${r.candidate_name} · ${r.status} · ${money(r.final_accepted_amount, r.currency || "USD")}`;
  if (key === "placements") return `${r.candidate_name} @ ${r.client_name} · ${r.status || "active"}`;
  if (key === "invoices") return `${r.invoice_number} · ${r.client_name} · ${money(r.total, r.currency || "USD")} · ${r.status}`;
  if (key === "leads") return `${r.company_name || r.name} · ${r.overall_status || ""}`;
  if (key === "outreach") return `${r.company_name || r.channel || ""} · ${r.outreach_type || ""}`;
  if (key === "replies") return `${r.company_name || ""} · ${r.reply_type}`;
  if (key === "handoffs") return `${r.client_name || ""} · ${r.decision || "pending"}`;
  if (key === "attendance") return `${r.date} · in ${r.check_in || "—"} / out ${r.check_out || "—"}`;
  if (key === "leaves") return `${r.leave_type} · ${r.start_date}→${r.end_date} · ${r.status}`;
  if (key === "payslips") return `${r.period} · ${money(r.net_pay, r.currency || "USD")}`;
  if (key === "tasks_open") return `${r.title} · due ${fmtDate(r.due_at)}`;
  return r.name || r.title || r.id;
}

function detailRoute(key, r) {
  if (key === "clients") return `/clients/${r.id}`;
  if (key === "jobs" || key === "assigned_jobs") return `/jobs/${r.id}`;
  if (key === "candidates") return `/candidates/${r.id}`;
  if (key === "placements") return `/placements`;
  if (key === "offers") return `/offers`;
  if (key === "submissions") return `/submissions`;
  if (key === "invoices") return `/revenue`;
  if (key === "interviews") return `/interviews`;
  if (key === "tasks_open") return `/tasks`;
  return null;
}
